import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { HttpService } from '../http/http.service';
import { Login } from '../../models/auth/login';
import { LoginResponse } from '../../models/auth/login.response';
import { Router } from '@angular/router';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { HttpErrorResponse } from '@angular/common/http';
import { LocalStorageService } from '../local-storage/local-storage.service';
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  AuthUser,
  AuthState,
  ApiError,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  VerifyEmailRequest,
  ResendVerificationRequest,
  ApiResponse,
  ProfileResponse,
} from '../../models/auth/auth.model';
import { SharedService, UserData } from '../shared/shared.service';
import { AuthUserState, StateService } from '../shared/state.service';
interface ForgotPasswordResponse {
  statusCode: number;
  message: string;
  data?: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public showLoader: boolean = false;

  httpService = inject(HttpService);
  router = inject(Router);
  private readonly storage = inject(LocalStorageService);
  private readonly sharedService = inject(SharedService);
  private readonly stateService = inject(StateService);

  constructor() {
    this.loadAuthStateFromStorage();
  }
  // Signals for reactive state management
  private readonly _authState = signal<AuthState>({
    user: null,
    role: null,
    username: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  });

  // Public readonly signals
  readonly authState = this._authState.asReadonly();
  readonly user = computed(() => this._authState().user);
  readonly token = computed(() => this._authState().token);
  readonly role = computed(() => this._authState().user?.role);
  readonly isAuthenticated = computed(() => this._authState().isAuthenticated);
  readonly isLoading = computed(() => this._authState().isLoading);
  readonly error = computed(() => this._authState().error);
  readonly name = computed(() => this._authState().user?.name);
  readonly username = computed(() => this._authState().user?.username);
  readonly email = computed(() => this._authState().user?.email);
  readonly canAccess = computed(
    () =>
      this._authState().user?.role === 'admin' ||
      this._authState().user?.role === 'administrator' ||
      this._authState().user?.role === 'supervisor'
  );

  //   isAuthenticatedd = false;
  // async initialize(): Promise<void> {
  //   const token = localStorage.getItem('auth_token');
  //   if (token) {
  //     // Optionally validate token here
  //     this.isAuthenticatedd = true;
  //   //  this.isAuthenticated = computed(() => this._authState().isAuthenticated);

  //   }
  // }
  // Login method
  login(credentials: LoginRequest): Observable<AuthResponse> {
    this.setLoading(true);
    this.clearError();

    return this.httpService.post<AuthResponse>(`auth/signin`, credentials).pipe(
      tap((response: AuthResponse) => {

        console.log('response', response);
        this.handleAuthSuccess(response);
      }),
      catchError((error: HttpErrorResponse) => {
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  // Register method
  register(userData: RegisterRequest): Observable<AuthResponse> {
    this.setLoading(true);
    this.clearError();

    return this.httpService.post<AuthResponse>(`auth/signup`, userData).pipe(
      tap((response: AuthResponse) => {
        // Don't navigate here - let the component handle displaying
        // the success message and then redirecting to login
        this.setLoading(false);
      }),
      catchError((error: HttpErrorResponse) => {
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  getProfile(): Observable<ProfileResponse> {
    return this.httpService.get<ProfileResponse>('auth/profile').pipe(
      tap((response: ProfileResponse) => {
        // this.handleAuthSuccess(response);
      }),
      catchError((error: HttpErrorResponse) => {
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  createUser(reg: any): Observable<LoginResponse> {
    return this.httpService.post<LoginResponse>('auth/user', reg).pipe();
  }
  forgotPassword(email: string): Observable<ForgotPasswordResponse> {
    this.showLoader = true;
    return this.httpService
      .post<ForgotPasswordResponse>('auth/forgot-password', { email })
      .pipe(
        tap((response: ForgotPasswordResponse) => {
          this.showLoader = false;
          if (response.statusCode === 200) {
            // this.toastrSuccess(response.message);
          }
        }),
        catchError(error => {
          this.showLoader = false;
          // this.toastrError(error.error?.message || 'Failed to process request');
          return throwError(() => error);
        })
      );
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    this.showLoader = true;
    return this.httpService
      .post('auth/reset-password', { token, newPassword })
      .pipe(
        tap((response: any) => {
          this.showLoader = false;
          if (response.statusCode === 200) {
            // this.toastrSuccess(response.message);
          }
        }),
        catchError(error => {
          this.showLoader = false;
          // this.toastrError(error.error?.message || 'Failed to process request');
          return throwError(() => error);
        })
      );
  }

  verifyEmail(token: string): Observable<any> {
    return this.httpService.post('auth/verify-email', { token: token }).pipe();
  }

  changePassword(oldPassword: string, newPassword: string): Observable<any> {
    this.showLoader = true;
    return this.httpService
      .post('auth/change-password', { oldPassword, newPassword })
      .pipe(
        tap((response: any) => {
          this.showLoader = false;
          if (response.statusCode === 200) {
            // this.toastrSuccess(response.message);
          }
        }),
        catchError(error => {
          this.showLoader = false;
          // this.toastrError(error.error?.message || 'Failed to process request');
          return throwError(() => error);
        })
      );
  }

  logout(): void {
    // Clear all stored data
    console.log('logout2');
    this.storage.removeItem('auth_token');
    this.storage.removeItem('auth_user');
    this.storage.removeItem('ADMIN');

    // Clear SharedService signals
    this.sharedService.clearUser();

    // Clear StateService signals
    this.stateService.clearState();

    // Navigate to login page
    this.router.navigate(['/auth/login']);
  }

  handleError(error: HttpErrorResponse) {
    // console.log(error);
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `,
        error.error
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(
      () => new Error('Something bad happened; please try again later.')
    );
  }

  // Get current access token from storage
  getAccessToken(): string | null {
    return this.storage.getItem('auth_token');
  }

  // Private helper methods
  private handleAuthSuccess(
    response: AuthResponse,
    navigate: boolean = true
  ): void {
    // Update state
    this._authState.set({
      user: response.user,
      role: response.user?.role,
      username: response.user?.username,
      token: response.token,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
    // console.log('handleAuthSuccess', response);

    // Save to storage
    this.saveAuthToStorage(response);

    // Navigate to dashboard after successful auth
    if (navigate) {
      this.router.navigate(['/auth/login']);
    }
  }

  private handleAuthError(error: HttpErrorResponse): void {
    let errorMessage = 'An error occurred during authentication';

    if (error.error && error.error.message) {
      errorMessage = error.error.message;
    } else if (error.status === 401) {
      errorMessage = 'Invalid credentials';
    } else if (error.status === 400) {
      errorMessage = 'Invalid request data';
    } else if (error.status === 409) {
      errorMessage = 'User already exists';
    } else if (error.status === 0) {
      errorMessage = 'Unable to connect to server';
    }

    this._authState.update(state => ({
      ...state,
      isLoading: false,
      error: errorMessage,
    }));
  }
  // Clear error
  clearError(): void {
    this._authState.update(state => ({
      ...state,
      error: null,
    }));
  }

  private setLoading(loading: boolean): void {
    this._authState.update(state => ({
      ...state,
      isLoading: loading,
    }));
  }

  private saveAuthToStorage(response: AuthResponse): void {
    // console.log('saveAuthToStorage', response);
    this.storage.setItem('auth_token', response.token);
    this.storage.setItem('auth_user', response.user);

    // Sync SharedService signals immediately after login
    // Map AuthUser to UserData for SharedService
    const userData: UserData = {
      id: response.user.id,
      username: response.user.username,
      email: response.user.email,
      phone: response.user.phone || '',
      timezone: response.user.timezone || '',
      isEmailVerified: response.user.isEmailVerified,
      role: response.user.role,
      roles: response.user.roles || [response.user.role],
      joinDate: response.user.joinDate || new Date(),
      companyId: response.user.companyId || 0,
      approverId: response.user.approverId || 0,
      employeeId: response.user.employeeId || 0,
    };

    this.sharedService.setToken(response.token);
    this.sharedService.setUser(userData);

    // Sync StateService as well
    const authUserState: AuthUserState = {
      id: response.user.id,
      email: response.user.email,
      firstName: response.user.firstName,
      lastName: response.user.lastName,
      timezone: response.user.timezone || '',
      role: response.user.role,
      companyId: response.user.companyId || 0,
      company: (response.user as any).company || '',
      employeeId: response.user.employeeId || 0,
      approverId: response.user.approverId || 0,
    };
    this.stateService.setAuthState(response.token, authUserState);
  }

  private loadAuthStateFromStorage(): void {
    const token = this.storage.getItem<string>('auth_token');
    const user = this.storage.getItem<AuthUser>('auth_user');

    if (token && user) {
      if (token) {
        this._authState.set({
          user,
          role: user.role,
          username: user.username,
          token: token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        this.clearAuthStorage();
      }
    }
  }

  private clearAuthStorage(): void {
    this.storage.removeItem('auth_token');
    this.storage.removeItem('auth_user');
  }
}
